/**
 * Server-side preferences and small features (spec §15): templates, trusted senders, contacts,
 * muted threads, snooze, outbox / send-undo, labels, split counters, quota.
 */
import { api, ApiError } from '$lib/api/client';
import { db } from '$lib/db';
import { sortLabels } from '$lib/mail/labels';
import { schedulePresets, snoozePresets, type SnoozePreset } from '$lib/mail/snooze';
import type { ComposeDraft } from '$lib/types';
import type { MailStore } from './mail.svelte';

export const SPLIT_COUNT_MIN_MS = 4000;

export class PrefsModule {
  private splitAt = 0;
  private sendTimer: ReturnType<typeof setInterval> | undefined;
  constructor(private readonly s: MailStore) {}

  // ---- templates ----------------------------------------------------------------------------

  async loadTemplates(): Promise<void> {
    try {
      this.s.templates = (await api.templates()).templates;
    } catch {
      /* offline */
    }
  }

  async saveTemplate(name: string, body: string): Promise<boolean> {
    try {
      this.s.templates = (await api.addTemplate(name, body)).templates;
      this.s.showToast('templateSaved');
      return true;
    } catch (err) {
      this.s.showToast((err as { status?: number })?.status === 409 ? 'templateTooMany' : 'actionFailed');
      return false;
    }
  }

  async removeTemplate(id: string): Promise<void> {
    try {
      this.s.templates = (await api.deleteTemplate(id)).templates;
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  // ---- trusted senders ----------------------------------------------------------------------

  async loadTrusted(): Promise<void> {
    try {
      this.s.trustedSenders = (await api.senders()).senders;
    } catch {
      /* offline */
    }
  }

  isTrusted(addr: string | null | undefined): boolean {
    const a = (addr || '').trim().toLowerCase();
    return !!a && this.s.trustedSenders.some((x) => x.name === a);
  }

  async trustSender(addr: string): Promise<void> {
    const a = (addr || '').trim().toLowerCase();
    if (!a) return;
    try {
      this.s.trustedSenders = (await api.trustSender(a)).senders;
      this.s.showToast('senderTrusted');
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  async untrustSender(id: string): Promise<void> {
    try {
      this.s.trustedSenders = (await api.untrustSender(id)).senders;
      this.s.showToast('senderUntrusted');
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  // ---- contacts -----------------------------------------------------------------------------

  /** Cache first, then the server list mirrored into IndexedDB. */
  async loadContacts(): Promise<void> {
    this.s.contacts = await db.contacts.toArray();
    if (this.s.offline) return;
    try {
      const { contacts } = await api.contacts();
      await db.contacts.clear();
      await db.contacts.bulkPut(contacts);
      this.s.contacts = contacts;
    } catch {
      /* keep the cached list */
    }
  }

  private async replaceContacts(list: { id: string; name: string; email: string }[]): Promise<void> {
    this.s.contacts = list;
    await db.contacts.clear();
    await db.contacts.bulkPut(list.map((c) => ({ ...c })));
  }

  async addContact(name: string, email: string): Promise<boolean> {
    try {
      await this.replaceContacts((await api.addContact(name, email)).contacts);
      return true;
    } catch {
      return false;
    }
  }

  async deleteContact(id: string): Promise<boolean> {
    try {
      await this.replaceContacts((await api.deleteContact(id)).contacts);
      return true;
    } catch {
      return false;
    }
  }

  async importContacts(): Promise<number | null> {
    try {
      const r = await api.importContacts();
      await this.loadContacts();
      return r.imported;
    } catch {
      return null;
    }
  }

  /** Reader "add sender": `contactExists` when already saved, else POST + `contactAdded`. */
  async addContactQuick(name: string, addr: string): Promise<void> {
    const email = (addr || '').trim().toLowerCase();
    if (!email) return;
    try {
      if (!this.s.contacts.length) await this.loadContacts();
      if (this.s.contacts.some((c) => c.email.trim().toLowerCase() === email)) {
        this.s.showToast('contactExists');
        return;
      }
      await this.replaceContacts((await api.addContact((name || '').trim() || email, email)).contacts);
      this.s.showToast('contactAdded');
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  // ---- muted --------------------------------------------------------------------------------

  async loadMuted(): Promise<{ id: string; subject: string }[]> {
    try {
      const r = await api.muted();
      this.s.mutedThreads = r.muted;
      this.s.mutedCount = r.muted.length;
      return r.muted;
    } catch {
      return [];
    }
  }

  async muteThread(uid: number, folder = this.s.currentFolder): Promise<{ moved: number } | null> {
    try {
      const r = await api.mute(folder, uid);
      this.s.mutedCount++;
      await this.s.syncFolder(folder, { reconcile: true }).catch(() => undefined);
      this.s.backToList();
      this.s.showToast(r.moved > 0 ? 'mutedToast' : 'mutedNoMove');
      return r;
    } catch {
      this.s.showToast('actionFailed');
      return null;
    }
  }

  async muteSelected(uids: number[] = [...this.s.selection]): Promise<{ moved: number; failed: number } | null> {
    const folder = this.s.selectionFolder || this.s.currentFolder;
    if (!uids.length) return null;
    let moved = 0;
    let failed = 0;
    for (const uid of uids) {
      try {
        moved += (await api.mute(folder, uid)).moved || 0;
      } catch {
        failed++;
      }
    }
    this.s.clearSelection();
    await this.loadMuted();
    await this.s.syncFolder(folder, { reconcile: true }).catch(() => undefined);
    this.s.showToast(failed && !moved ? 'actionFailed' : 'mutedToast');
    return { moved, failed };
  }

  async unmute(id: string): Promise<void> {
    try {
      await api.unmute(id);
      this.s.mutedThreads = this.s.mutedThreads.filter((m) => m.id !== id);
      this.s.mutedCount = Math.max(0, this.s.mutedCount - 1);
      this.s.showToast('unmutedToast');
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  // ---- snooze -------------------------------------------------------------------------------

  snoozeOptions(): SnoozePreset[] {
    return snoozePresets();
  }

  scheduleOptions(): SnoozePreset[] {
    return schedulePresets();
  }

  async snooze(uids: number[], until: number): Promise<boolean> {
    const folder = this.s.currentFolder;
    try {
      await api.snooze(folder, uids, until);
      await this.s.syncFolder(folder, { reconcile: true }).catch(() => undefined);
      this.s.clearSelection();
      if (this.s.screen.name === 'read' && uids.includes(this.s.screen.uid)) this.s.backToList();
      this.s.showToast('snoozed');
      return true;
    } catch {
      this.s.showToast('actionFailed');
      return false;
    }
  }

  async unsnooze(uids: number[]): Promise<boolean> {
    try {
      await api.unsnooze(uids);
      await this.s.syncFolder(this.s.currentFolder, { reconcile: true }).catch(() => undefined);
      this.s.clearSelection();
      this.s.showToast('unsnoozed');
      return true;
    } catch {
      this.s.showToast('actionFailed');
      return false;
    }
  }

  // ---- outbox / send undo -------------------------------------------------------------------

  async loadOutbox(): Promise<void> {
    try {
      this.s.pendingSends = (await api.outbox()).pending;
    } catch {
      /* offline */
    }
  }

  /** Stop the undo countdown (shutdown / test teardown). */
  dispose(): void {
    clearInterval(this.sendTimer);
    this.sendTimer = undefined;
  }

  /** Countdown toast after a queued send; `left` seconds tick down to 0. */
  startSendUndo(id: string, seconds: number): void {
    clearInterval(this.sendTimer);
    this.s.pendingSend = { id, left: seconds };
    this.sendTimer = setInterval(() => {
      const cur = this.s.pendingSend;
      if (!cur) {
        clearInterval(this.sendTimer);
        return;
      }
      const left = cur.left - 1;
      if (left <= 0) {
        this.s.pendingSend = null;
        clearInterval(this.sendTimer);
      } else this.s.pendingSend = { id, left };
    }, 1000);
  }

  /** `outbox.state` event: drop finished items; close the undo toast once sent. */
  applyOutboxState(ev: { outboxId: string; state: string; sendAt?: number }): void {
    const id = ev.outboxId;
    if (ev.state === 'sent' || ev.state === 'failed' || ev.state === 'cancelled') {
      this.s.pendingSends = this.s.pendingSends.filter((p) => p.id !== id);
      if (this.s.pendingSend?.id === id) {
        this.s.pendingSend = null;
        clearInterval(this.sendTimer);
      }
    } else if (ev.state === 'queued' && typeof ev.sendAt === 'number' && !this.s.pendingSends.some((p) => p.id === id)) {
      void this.loadOutbox();
    }
  }

  private draftFrom(d: { to: string; cc?: string; bcc?: string; subject: string; text?: string; html?: string }): ComposeDraft {
    return { to: d.to, cc: d.cc || undefined, bcc: d.bcc || undefined, subject: d.subject, text: d.text || undefined, html: d.html || undefined };
  }

  /** Undo-send toast button. */
  async cancelSend(): Promise<boolean> {
    const pending = this.s.pendingSend;
    if (!pending) return false;
    this.s.pendingSend = null;
    clearInterval(this.sendTimer);
    try {
      const r = await api.cancelSend(pending.id);
      this.s.openCompose(this.draftFrom(r.draft));
      this.s.showToast('sendCancelled');
      return true;
    } catch (err) {
      this.s.showToast(err instanceof ApiError && err.status === 409 ? 'alreadySent' : 'actionFailed');
      return false;
    }
  }

  /** Outbox screen cancel button. */
  async cancelScheduled(id: string): Promise<boolean> {
    try {
      const r = await api.cancelSend(id);
      this.s.pendingSends = this.s.pendingSends.filter((p) => p.id !== id);
      this.s.openCompose(this.draftFrom(r.draft));
      this.s.showToast('sendCancelled');
      return true;
    } catch (err) {
      this.s.showToast(err instanceof ApiError && err.status === 409 ? 'alreadySent' : 'actionFailed');
      await this.loadOutbox().catch(() => undefined);
      return false;
    }
  }

  // ---- labels -------------------------------------------------------------------------------

  async loadLabels(): Promise<void> {
    try {
      const r = await api.labels(this.s.currentFolder);
      this.s.knownLabels = r.inUse ?? r.labels ?? [];
      this.s.labelCounts = r.counts ?? {};
    } catch {
      /* offline */
    }
  }

  /** Add/remove a label on uids of the current folder; restores flags on failure. */
  async setLabel(uids: number[], label: string, on: boolean): Promise<boolean> {
    const folder = this.s.currentFolder;
    const before = new Map<number, string[]>();
    for (const m of this.s.msgs) if (m.folder === folder && uids.includes(m.uid)) before.set(m.uid, [...m.flags]);
    try {
      const r = await api.actions(folder, uids, on ? 'label' : 'unlabel', undefined, label);
      if (!r.ok) throw new ApiError(502, 'label_failed');
      await this.s.syncFolder(folder).catch(() => undefined);
      if (on && !this.s.knownLabels.includes(label)) this.s.knownLabels = sortLabels([...this.s.knownLabels, label]);
      this.s.showToast(on ? 'labelAdded' : 'labelRemoved');
      return true;
    } catch {
      for (const [uid, flags] of before) {
        const row = this.s.msgs.find((m) => m.uid === uid && m.folder === folder);
        if (row) row.flags = flags;
      }
      this.s.showToast('actionFailed');
      return false;
    }
  }

  // ---- split counters / quota ---------------------------------------------------------------

  /** Rail personal/list unseen counters, throttled to once per 4 s unless forced. */
  async loadSplitCounts(force = false): Promise<void> {
    const now = Date.now();
    if (!force && now - this.splitAt < SPLIT_COUNT_MIN_MS) return;
    this.splitAt = now;
    try {
      const [p, l] = await Promise.all([api.searchCount('INBOX', { personal: '1', unseen: '1' }), api.searchCount('INBOX', { list: '1', unseen: '1' })]);
      this.s.splitUnread = { personal: p.matched ?? 0, list: l.matched ?? 0 };
    } catch {
      /* keep the previous counters */
    }
  }

  async loadQuota(): Promise<void> {
    try {
      const q = await api.quota();
      this.s.quota = q.supported && q.limit ? { supported: true, used: q.used ?? 0, limit: q.limit } : { supported: false, used: 0, limit: 0 };
    } catch {
      this.s.quota = null;
    }
  }
}
